import { Minus, Plus, Play, Square } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { pitchClass } from '@/lib/pitch';
import { TIMBRES } from '@/lib/droneTimbres';
import { QUALITIES, DRONE_KEYS, shiftProg } from '@/lib/droneChords';
import { DroneProgression } from '@/components/tuner/DroneProgression';

const stepBtn = 'h-10 w-10 shrink-0 rounded-lg border border-white/10 flex items-center justify-center hover:bg-white/5 disabled:opacity-30 active:scale-95 transition-transform';
const Label = ({ children }) => <span className="text-[10px] uppercase tracking-[0.3em] text-white/45">{children}</span>;
const VOICINGS = [{ v: 'root', label: 'Fondamentale' }, { v: 'fifth', label: '+ Quinta' }, { v: 'chord', label: 'Accordo' }];

const Choice = ({ testid, selected, onClick, children, tone = 'pink' }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`h-10 px-2 rounded-xl text-[10px] sm:text-[11px] uppercase tracking-[0.1em] transition-colors ${selected ? (tone === 'sky' ? 'bg-sky-400 text-black' : 'bg-pink-400 text-black') : 'border border-white/10 text-white/70 hover:bg-white/5'}`}
  >
    {children}
  </button>
);

export const DronePanel = ({ drone, flats }) => {
  const { cfg, update, active, toggle, voicing, timbre, progOn, prog } = drone;
  // With the chord sequence on, the note is its key: the chords follow it.
  const setNote = (pc) => update(progOn ? { pc, prog: { ...prog, ...shiftProg(prog, pc) } } : { pc });
  return (
    <section data-testid="tuner-drone" className={`bg-[#111112] border rounded-2xl p-4 sm:p-6 flex flex-col gap-4 transition-colors ${active ? 'border-sky-400/40 shadow-[0_0_24px_rgba(56,189,248,0.12)]' : 'border-white/5'}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col">
          <Label>Bordone · {progOn ? 'a tempo' : 'nota fissa'}</Label>
          <span className="text-[11px] text-white/40 mt-1">Consigliate le cuffie · i tasti MIDI cambiano la nota</span>
        </div>
        <div className="flex items-center gap-3">
          {active && <span data-testid="tuner-drone-current" className="font-mono font-bold text-2xl leading-none text-sky-300">{drone.label}</span>}
          <button
            data-testid="tuner-drone-toggle"
            onClick={toggle}
            aria-label={active ? 'Ferma bordone' : 'Avvia bordone'}
            className={`h-12 px-4 shrink-0 rounded-full flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] active:scale-95 transition-[transform,background-color] ${active ? 'bg-sky-400 text-black' : 'bg-pink-400 text-black'}`}
          >
            {active ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {active ? 'Stop' : 'Play'}
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Label>Strumento in</Label>
        <div className="grid grid-cols-4 gap-1.5">
          {DRONE_KEYS.map((k) => (
            <Choice key={k.id} testid={`tuner-drone-key-${k.id}`} selected={(cfg.transpose || 'C') === k.id} onClick={() => update({ transpose: k.id })} tone="sky"><span className="normal-case font-mono text-sm">{k.label}</span></Choice>
          ))}
        </div>
        <span className="text-[11px] text-white/35">Note, accordi, scala ed esercizi sono scritti per lo strumento scelto; il bordone suona in suoni reali.</span>
      </div>

      <div className="flex flex-col gap-2">
        <Label>Timbro</Label>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">
          {TIMBRES.map((t) => (
            <Choice key={t.id} testid={`tuner-drone-timbre-${t.id}`} selected={timbre === t.id} onClick={() => update({ timbre: t.id })} tone="sky">{t.label}</Choice>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Label>{progOn ? 'Tonalità della sequenza' : 'Nota'}</Label>
        <div className="grid grid-cols-6 gap-1.5 sm:gap-2">
          {Array.from({ length: 12 }, (_, pc) => (
            <button
              key={pc}
              data-testid={`tuner-drone-note-${pc}`}
              onClick={() => setNote(pc)}
              className={`h-11 rounded-xl border font-bebas text-xl tracking-wide transition-colors ${cfg.pc === pc ? 'bg-sky-400 text-black border-sky-400' : 'border-white/10 text-white/80 hover:border-white/30'}`}
            >
              {pitchClass(pc, flats)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Label>Ottava</Label>
        <button data-testid="tuner-drone-octave-down" aria-label="Ottava più bassa" disabled={cfg.octave <= 1} onClick={() => update({ octave: cfg.octave - 1 })} className={stepBtn}><Minus className="w-4 h-4" /></button>
        <span data-testid="tuner-drone-note-label" className="w-12 text-center font-bebas text-2xl">{pitchClass(cfg.pc, flats)}{cfg.octave}</span>
        <button data-testid="tuner-drone-octave-up" aria-label="Ottava più alta" disabled={cfg.octave >= 5} onClick={() => update({ octave: cfg.octave + 1 })} className={stepBtn}><Plus className="w-4 h-4" /></button>
      </div>

      <div className="flex flex-col gap-2">
        <Label>Suona</Label>
        <div className="grid grid-cols-3 gap-1.5">
          {VOICINGS.map((o) => (
            <Choice key={o.v} testid={`tuner-drone-voicing-${o.v}`} selected={voicing === o.v} onClick={() => update({ voicing: o.v })}>{o.label}</Choice>
          ))}
        </div>
        {voicing === 'chord' && !progOn && (
          <div className="grid grid-cols-4 sm:grid-cols-7 gap-1.5">
            {Object.entries(QUALITIES).map(([q, d]) => (
              <Choice key={q} testid={`tuner-drone-quality-${q}`} selected={(cfg.quality || 'maj') === q} onClick={() => update({ quality: q })} tone="sky">{d.name}</Choice>
            ))}
          </div>
        )}
      </div>

      <DroneProgression drone={drone} flats={flats} />

      <div className="flex flex-col gap-2">
        <Label>Intervalli misurati su</Label>
        <div className="grid grid-cols-2 gap-1.5">
          {[{ v: 'equal', label: 'Temperamento equabile' }, { v: 'just', label: 'Intonazione naturale' }].map((o) => (
            <Choice key={o.v} testid={`tuner-temperament-${o.v}`} selected={(cfg.temperament || 'equal') === o.v} onClick={() => update({ temperament: o.v })}>{o.label}</Choice>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <span className="text-[10px] uppercase tracking-[0.2em] text-white/40 w-16">Volume</span>
        <Slider data-testid="tuner-drone-volume" value={[cfg.volume * 100]} min={0} max={100} step={1} onValueChange={(v) => update({ volume: v[0] / 100 })} className="flex-1" />
        <span data-testid="tuner-drone-volume-value" className="w-10 text-right font-mono text-xs text-white/50">{Math.round(cfg.volume * 100)}%</span>
      </div>
    </section>
  );
};
